// Класс-ErrorBoundary для мониторинга (Remetry): ловит краши поддерева React
// и шлёт их в reportError. В app/_layout.tsx стоит САМЫМ внешним — снаружи
// AppProvider/TamaguiProvider, поэтому fallback ниже не может использовать
// useApp()/хуки и берёт язык из runtime-lang, а Tamagui поднимает сам (см. render).
import { Component, type ErrorInfo, type ReactNode } from 'react'
import { Platform } from 'react-native'
import { TamaguiProvider, Text, YStack } from 'tamagui'

import { config } from '../../tamagui.config'
import { Button } from './primitives'
import { STR } from '../i18n/strings'
import { getRuntimeLang } from '../i18n/runtime-lang'
import { reportError } from '../lib/monitoring'

type State = { error: Error | null }

export class ErrorBoundary extends Component<{ children: ReactNode }, State> {
  state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    reportError(error, { componentStack: info.componentStack ?? undefined })
  }

  reload = (): void => {
    if (Platform.OS === 'web') {
      location.reload()
    } else {
      // сброс стейта размонтирует упавшее поддерево и создаст его заново
      this.setState({ error: null })
    }
  }

  render(): ReactNode {
    if (!this.state.error) return this.props.children

    const lang = getRuntimeLang()
    return (
      // Свой TamaguiProvider: этот компонент рендерится снаружи Themed
      // (app/_layout.tsx), штатного провайдера тут нет. Монтируется только
      // при краше — в обычной работе приложения оверхеда нет.
      <TamaguiProvider config={config} defaultTheme="light">
        <YStack
          flex={1}
          flexBasis="auto"
          alignItems="center"
          justifyContent="center"
          gap={12}
          padding={24}
          backgroundColor="$bgLayout"
        >
          <Text fontSize={26} fontWeight="800" color="$color" textAlign="center">
            {STR['crash.title'][lang]}
          </Text>
          <Text fontSize={14} color="$muted" textAlign="center">
            {STR['crash.body'][lang]}
          </Text>
          {/* role="button" — accessibilityState на вебе утекает в DOM, а состояния тут нет */}
          <Button variant="primary" role="button" onPress={this.reload}>
            {STR['crash.reload'][lang]}
          </Button>
        </YStack>
      </TamaguiProvider>
    )
  }
}
